import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { NewProfileFrame } from "../NewProfileFrame";
import { H2, H3 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import { Button } from "../../../components/Button";
import { Box } from "../../../components/Box";
import { Card } from "../../../components/Card";
import { Flex } from "../../../components/Flex";
import { ROUTE } from "../../../routes";
import { useCountries } from "../../../data/useCountries";
import { useEntityTypes } from "../../../data/useEntityTypes";
import { capitalise } from "../../../utils/string";
import { useCreateProfile } from "../../../data/useCreateProfile";
import { AddressPayload, CreateProfileRequest } from "../../../types/Profile";
import { useGetAddressTypeList } from "../../../data/useGetAddressTypeList";
import { useState } from "react";
import { Modal } from "../../../components/Modal";
import { useCompanyStatusOptions } from "../../../data/fallbackPayloads/useCompanyStatusOptions";
import { useAuth } from "../../../context/AuthContext";

const Header = () => (
  <>
    <H2>Entity Summary</H2>
    <Paragraph>
      Please check the details of this entity before creating.
    </Paragraph>
  </>
);

type LocationState = {
  newEntity?: {
    division?: number | string;
    team?: number | string;
    name: string;
    country: number | null;
    entityType: number | null;
    status: string;
    yourReference: string;
    registrationNumber: string;
    dateOfRegistration: string;
  };
  registeredAddress?: {
    flat_appartment_subbuilding?: string;
    building_number?: string;
    building_name?: string;
    road_street?: string;
    town_city?: string;
    district?: string;
    state_province_name?: string;
    state_province_code?: string;
    post_zip_code?: string;
    country_code?: string;
    country_name?: string;
  };
};

export const NewEntitySummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { newEntity, registeredAddress } = (location.state ||
    {}) as LocationState;

  const { data: countries } = useCountries();
  const { data: entityTypes } = useEntityTypes();
  const { options: companyStatusOptions } = useCompanyStatusOptions();
  const { mutate: createProfile, isLoading } = useCreateProfile();
  const { data: addressTypes } = useGetAddressTypeList("all");
  const { hasPermission } = useAuth();

  const [showSuccess, setShowSuccess] = useState(false);

  const registeredId = addressTypes?.find(
    (t: any) => t.name === "Registered",
  )?.id;

  const handleBack = () => {
    navigate(ROUTE.PROFILE_NEW_ENTITY_ADDRESS, {
      state: { newEntity, registeredAddress },
    });
  };

  const handleCreate = () => {
    if (!newEntity) return;

    const dateOfIncorporation = newEntity.dateOfRegistration
      ? DateTime.fromISO(newEntity.dateOfRegistration).toFormat("yyyy-MM-dd")
      : null;

    const addressPayload: AddressPayload[] = [];

    if (registeredAddress) {
      const cc = registeredAddress.country_code ?? null;

      addressPayload.push({
        _type: registeredId ?? undefined,
        country: cc,
        country_code: cc,
        building_name: registeredAddress.building_name ?? "",
        building_number: registeredAddress.building_number ?? "",
        flat_appartment_subbuilding:
          registeredAddress.flat_appartment_subbuilding ?? "",
        road_street: registeredAddress.road_street ?? "",
        line1: "",
        line2: "",
        district: registeredAddress.district ?? "",
        town_city: registeredAddress.town_city ?? "",
        state: "",
        state_province_code: registeredAddress.state_province_code ?? "",
        state_province_name: registeredAddress.state_province_name ?? "",
        post_zip_code: registeredAddress.post_zip_code ?? "",
        postcode: registeredAddress.post_zip_code ?? "",
      });
    }

    const payload: CreateProfileRequest = {
      _type: "entity",
      primary_division: newEntity.division as number,
      primary_team: newEntity.team as number,
      name: newEntity.name,
      company_status: newEntity.status ? Number(newEntity.status) : null,
      entity_type: newEntity.entityType ?? null,
      external_reference: newEntity.yourReference || null,
      registration_number: newEntity.registrationNumber || null,
      date_of_incorporation: dateOfIncorporation,
      country: newEntity.country ?? null,
      default: false,
      address: addressPayload,
      relation: [],
    };

    createProfile(payload, {
      onSuccess: (res: any) => {
        const id = res.data?.id ?? res.data?.data?.id;
        if (id) {
          setShowSuccess(true);
        } else {
          navigate(ROUTE.PROFILES);
        }
      },
      onError: (err) => {
        console.error("Failed to create entity profile", err);
      },
    });
  };

  const formattedDate =
    newEntity?.dateOfRegistration &&
    DateTime.fromISO(newEntity.dateOfRegistration).toFormat("dd/MM/yyyy");

  const fullAddress = registeredAddress
    ? [
        registeredAddress.flat_appartment_subbuilding,
        registeredAddress.building_number,
        registeredAddress.building_name,
        registeredAddress.road_street,
        registeredAddress.town_city,
        registeredAddress.district,
        registeredAddress.post_zip_code,
      ]
        .filter(Boolean)
        .join(", ")
    : "No address provided";

  const countryObj =
    newEntity?.country != null && countries?.data
      ? countries.data.find((c: any) => c.id === newEntity.country)
      : undefined;

  const countryLabel =
    countryObj?.name || registeredAddress?.country_name || "-";
  const countryFlag = countryObj?.icon;

  const entityTypeObj =
    newEntity?.entityType != null && entityTypes?.data
      ? entityTypes.data.find((t: any) => t.id === newEntity.entityType)
      : null;
  const entityTypeName = entityTypeObj?.name ?? "-";
  const companyStatusLabel =
    newEntity?.status != null && newEntity.status !== ""
      ? companyStatusOptions.find(
          (option: any) => option.value === Number(newEntity.status),
        )?.label ?? newEntity.status
      : null;

  // const navigateToNewEntity = () => {
  //   setShowSuccess(false);
  //   navigate(ROUTE.PROFILES);
  // };

  const navigateToProfiles = () => {
    setShowSuccess(false);
    navigate(ROUTE.PROFILES);
  };

  return (
    <NewProfileFrame mode="entity" step="createProfile">
      <Header />

      <Box mt={3} maxWidth={650}>
        <Card p={3}>
          <Flex justifyContent="space-between" mb={2}>
            <Box>
              <H3 fontSize={2} style={{"word-break": "break-all"}}>{newEntity?.name || "-"}</H3>
              {companyStatusLabel ? (
                <Paragraph fontSize={1} color="gray.60">
                  {companyStatusLabel}
                </Paragraph>
              ) : (
                <Paragraph fontSize={1} color="gray.60">
                  No current status
                </Paragraph>
              )}
            </Box>
          </Flex>

          <Paragraph fontSize={1} color="gray.60">
            {fullAddress || "No address provided"}
          </Paragraph>

          {countryLabel && countryLabel !== "-" && (
            <Flex gap="6" alignItems="center" mt={1}>
              {countryFlag && (
                <img src={`data:image/png;base64,${countryFlag}`} alt="flag" />
              )}
              <Paragraph fontSize={1} color="gray.60" my={0}>
                {capitalise(countryLabel)}
              </Paragraph>
            </Flex>
          )
          //  : (
          //   <Paragraph fontSize={1} color="gray.60" mt={1}>
          //     -
          //   </Paragraph>
          // )
          }

          <Flex justifyContent="space-between" mt={2}>
            <Paragraph fontSize={1} color="gray.60" my={0}>
              {entityTypeName}
              {formattedDate && ` • ${formattedDate}`}
            </Paragraph>
            <Paragraph fontSize={1} color="gray.60" my={0}>
              {newEntity?.registrationNumber || "No reg. number"}
            </Paragraph>
          </Flex>
        </Card>

        <Flex justifyContent="space-between" mt={3}>
          <Button variant="text" onClick={handleBack}>
            Back
          </Button>
          <Button
            onClick={handleCreate}
            isDisabled={isLoading || !hasPermission("profile.create")}
            title={
              hasPermission("profile.create")
                ? undefined
                : "You don't have permission to create profiles"
            }
          >
            {isLoading ? "Creating..." : "Create"}
          </Button>
        </Flex>
      </Box>

      <Modal
        isOpen={showSuccess}
        onClose={navigateToProfiles}
        title="Entity created"
        content="Thank you for creating a new entity profile. Please create another entity or close to be taken to this entity record."
        onModalConfirmClick={navigateToProfiles}
        // secondaryButtonText="Close"
        primaryButtonText="Close"
        primaryButtonVariant="primary"
        // hasSecondaryButton
        // onSecondaryButtonClick={navigateToProfiles}
        width="450px"
        hasIcon
        isSuccessful
      />
    </NewProfileFrame>
  );
};

export default NewEntitySummary;
